import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  emptyPlanOutput,
  emptyPlanStage,
  type ProcessPlanFormValues,
} from "@/features/planning/schema";
import {
  calculateCrm,
  calculateParting,
  DEFAULT_MILL_CONSTANTS,
  filterCompatibleInventory,
  nestStrips,
  sortFifo,
  stripWidthFromOd,
  type WidthGroup,
} from "@/lib/mill";
import { cn } from "@/lib/utils";

type ProcessType = { id: string; code?: string; name: string };
type Machine = {
  id: string;
  process_type_id: string;
  status: string;
  max_width_mm?: number | null;
};
type Material = { id: string; name: string };
type ShapeSize = {
  id: string;
  size_label?: string;
  od?: number | null;
  thickness?: number | null;
};
type StockBatch = {
  id: string;
  batch_no: string;
  material_id: string;
  weight: number;
  quantity: number;
  process_path?: string | null;
  last_process_code?: string | null;
  received_at?: string;
  thickness?: number | null;
  width?: number | null;
  wt_per_m?: number | null;
  batch_status?: string;
  quality_status?: string | null;
};
type SalesOrder = {
  id: string;
  so_number?: string;
  lines?: {
    id: string;
    material_id: string;
    material_grade_id?: string | null;
    shape_size_id?: string | null;
    qty: number;
    weight: number;
    thickness?: number | null;
    length?: number | null;
  }[];
} | null;

type Dims = {
  thk?: number | null;
  width?: number | null;
  weight?: number | null;
  qty?: number | null;
  label?: string;
};

type JourneyStep = {
  id: string;
  name: string;
  status: "stock" | "in_draft" | "skipped" | "fg";
  note?: string;
  in?: Dims;
  out?: Dims;
  waste?: { widthMm?: number; weightT?: number; pct?: number; label?: string };
  detailLines?: string[];
};

type Suggestion = {
  stages: ProcessPlanFormValues["stages"];
  mother: {
    batchNo: string;
    weight: number;
    thk: number | null;
    width: number | null;
    wtPerMm: number;
    alreadyPickled: boolean;
  };
  salesOrderId?: string;
  salesOrderLineId?: string;
  soNumber?: string;
  journey: JourneyStep[];
  draftStageCount: number;
  totalWasteT: number;
};

function machineFor(processTypes: ProcessType[], machines: Machine[], code: string) {
  const pt = processTypes.find((p) => (p.code || "").toUpperCase() === code);
  if (!pt) return { processTypeId: "", machineId: "", name: code, maxWidth: null as number | null };
  const m = machines.find((x) => x.process_type_id === pt.id && x.status === "active");
  return {
    processTypeId: pt.id,
    machineId: m?.id ?? "",
    name: pt.name || code,
    maxWidth: m?.max_width_mm ?? null,
  };
}

function fmt(n: number | null | undefined, digits = 3): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return Number(v.toFixed(digits)).toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
}

function dimsLine(d?: Dims): string {
  if (!d) return "—";
  const bits: string[] = [];
  if (d.label) bits.push(d.label);
  if (d.thk != null) bits.push(`${fmt(d.thk, 2)} mm thk`);
  if (d.width != null) bits.push(`${fmt(d.width, 1)} mm W`);
  if (d.weight != null) bits.push(`${fmt(d.weight, 3)} T`);
  if (d.qty != null) bits.push(`qty ${fmt(d.qty, 0)}`);
  return bits.length ? bits.join(" · ") : "—";
}

function wasteLine(w?: JourneyStep["waste"]): string {
  if (!w) return "—";
  if (w.label) return w.label;
  const bits: string[] = [];
  if (w.widthMm != null && w.widthMm > 0) bits.push(`${fmt(w.widthMm, 1)} mm`);
  if (w.weightT != null && w.weightT > 0) bits.push(`${fmt(w.weightT, 3)} T`);
  if (w.pct != null && w.pct > 0) bits.push(`${fmt(w.pct, 1)}%`);
  return bits.length ? bits.join(" · ") : "0";
}

/** Pack coil into machine-safe width groups; leftover &lt; 20 mm stays as scrap. */
function suggestWidthGroups(coilWidth: number, maxPartWidth: number | null): WidthGroup[] {
  if (!(coilWidth > 0)) return [];
  const maxW = maxPartWidth && maxPartWidth > 0 ? maxPartWidth : Math.min(coilWidth, 600);
  const groups: WidthGroup[] = [];
  let rem = coilWidth;
  while (rem > 20) {
    if (rem <= maxW + 1e-6) {
      groups.push({ width: Math.round(rem * 10) / 10 });
      rem = 0;
      break;
    }
    groups.push({ width: Math.round(maxW * 10) / 10 });
    rem -= maxW;
  }
  if (!groups.length) groups.push({ width: Math.round(coilWidth * 10) / 10 });
  return groups;
}

function coilWtPerMm(coil: StockBatch): number {
  if (coil.wt_per_m && coil.wt_per_m > 0) return coil.wt_per_m;
  const w = coil.width ?? 0;
  if (w > 0) return (coil.weight * 1000) / w;
  return 0;
}

function buildSuggestion(input: {
  processTypes: ProcessType[];
  machines: Machine[];
  materials: Material[];
  stockBatches: StockBatch[];
  salesOrder?: SalesOrder;
  shapes?: ShapeSize[];
}): Suggestion | { error: string } {
  const { processTypes, machines, materials, stockBatches, salesOrder, shapes = [] } = input;
  const eligible = filterCompatibleInventory(stockBatches, "HR_PARTING");
  const fifo = sortFifo(eligible.length ? (eligible as StockBatch[]) : stockBatches);
  const coil = fifo[0];

  if (!coil) return { error: "No compatible pickled stock for HR parting" };

  const line = salesOrder?.lines?.[0];
  const matId = line?.material_id || coil.material_id || materials[0]?.id || "";
  const alreadyPickled =
    (coil.last_process_code || "").toUpperCase() === "PICKLING" ||
    (coil.process_path || "").includes("PICKLING");

  const coilWidth = coil.width ?? 0;
  const coilThk = coil.thickness ?? null;
  const wtmm = coilWtPerMm(coil);
  const stages: ProcessPlanFormValues["stages"] = [];
  const journey: JourneyStep[] = [];
  let totalWasteT = 0;

  journey.push({
    id: "mother",
    name: "Mother coil",
    status: "stock",
    note: alreadyPickled ? "FIFO · inbound pickled" : "FIFO stock",
    out: {
      thk: coilThk,
      width: coilWidth || null,
      weight: coil.weight,
      label: coil.batch_no,
    },
  });

  journey.push({
    id: "pickle",
    name: "Pickling",
    status: "skipped",
    note: alreadyPickled ? "Already done inbound — not staged" : "Usually outsourced — not staged",
    in: { thk: coilThk, width: coilWidth || null, weight: coil.weight },
    out: { thk: coilThk, width: coilWidth || null, weight: coil.weight },
    waste: { weightT: 0, label: "0 (skipped)" },
  });

  let curThk = coilThk;
  let curWidth = coilWidth;
  let curWeight = coil.weight;
  let curWtmm = wtmm;
  let partCount = 1;

  const parting = machineFor(processTypes, machines, "HR_PARTING");
  if (parting.processTypeId && curWidth > 0) {
    const globalCount = 2;
    const groups = suggestWidthGroups(curWidth, parting.maxWidth);
    const result = calculateParting({
      coilWidth: curWidth,
      coilWeight: curWeight,
      coilWtPerM: curWtmm,
      thickness: curThk,
      length: null,
      widthGroups: groups,
      globalCount,
      maxPartWidth: parting.maxWidth,
    });
    const scrapWt =
      curWidth > 0 ? (result.scrapWidth / curWidth) * curWeight : 0;
    totalWasteT += scrapWt;
    const groupLabel = groups.map((g) => `${fmt(g.width, 1)}×${globalCount}`).join(" + ");

    stages.push({
      ...emptyPlanStage,
      process_type_id: parting.processTypeId,
      machine_id: parting.machineId,
      input_source: "inventory",
      quality_check_required: true,
      inputs: [
        {
          inventory_batch_id: coil.id,
          planned_qty: coil.quantity || 1,
          planned_weight: coil.weight,
        },
      ],
      outputs: result.parts.map((p) => ({
        ...emptyPlanOutput,
        material_id: matId,
        material_grade_id: line?.material_grade_id ?? "",
        shape_size_id: line?.shape_size_id ?? "",
        planned_qty: 1,
        planned_weight: p.weight,
        thickness_mm: p.thickness ?? undefined,
        width_mm: p.width,
        notes: `Part ${p.suffix} · suggested A/B doubles`,
      })),
    });

    journey.push({
      id: "part",
      name: "HR Parting",
      status: "in_draft",
      note: `A/B doubles · ${groupLabel}`,
      in: { thk: curThk, width: curWidth, weight: curWeight },
      out: {
        qty: result.totalParts,
        weight: result.totalWeight,
        label: `${result.totalParts} parts`,
      },
      waste: {
        widthMm: result.scrapWidth,
        weightT: scrapWt,
        pct: curWidth > 0 ? (result.scrapWidth / curWidth) * 100 : 0,
      },
      detailLines: result.parts.map(
        (p) => `${p.suffix}: ${fmt(p.width, 1)} mm · ${fmt(p.weight, 3)} T`,
      ),
    });

    // Carry forward first part as representative for CRM→slit chain
    const first = result.parts[0];
    if (first) {
      curWidth = first.width;
      curWeight = first.weight;
      curWtmm = first.wt_per_m;
      partCount = result.totalParts;
    }
  } else if (parting.processTypeId) {
    stages.push({
      ...emptyPlanStage,
      process_type_id: parting.processTypeId,
      machine_id: parting.machineId,
      input_source: "inventory",
      quality_check_required: true,
      inputs: [
        {
          inventory_batch_id: coil.id,
          planned_qty: coil.quantity || 1,
          planned_weight: coil.weight,
        },
      ],
      outputs: [
        {
          ...emptyPlanOutput,
          material_id: matId,
          planned_qty: 1,
          planned_weight: coil.weight,
          thickness_mm: coilThk ?? undefined,
          width_mm: coilWidth || undefined,
          notes: "Suggested parting — set widths in stage assist",
        },
      ],
    });
    journey.push({
      id: "part",
      name: "HR Parting",
      status: "in_draft",
      note: "Coil width missing — refine in assist",
      in: { weight: curWeight, thk: curThk },
      out: { weight: curWeight },
      waste: { label: "set widths" },
    });
  }

  const crm = machineFor(processTypes, machines, "CRM");
  if (crm.processTypeId) {
    const targetThk =
      line?.thickness && line.thickness > 0
        ? line.thickness
        : curThk != null
          ? Math.round(curThk * 0.9 * 100) / 100
          : null;
    const crmOut =
      curThk != null && targetThk != null
        ? calculateCrm({
            weight: curWeight,
            thickness: curThk,
            targetThickness: targetThk,
            width: curWidth || null,
            wt_per_m: curWtmm || null,
          })
        : null;

    stages.push({
      ...emptyPlanStage,
      process_type_id: crm.processTypeId,
      machine_id: crm.machineId,
      input_source: "previous_stage",
      inputs: [],
      quality_check_required: true,
      outputs: [
        {
          ...emptyPlanOutput,
          material_id: matId,
          planned_qty: partCount,
          planned_weight: crmOut?.weight ?? curWeight,
          thickness_mm: crmOut?.thickness ?? targetThk ?? undefined,
          width_mm: curWidth || undefined,
          notes: "Suggested CRM · weight ≈ input",
        },
      ],
    });

    journey.push({
      id: "crm",
      name: "CRM",
      status: "in_draft",
      note:
        curThk != null && targetThk != null
          ? `${fmt(curThk, 2)} → ${fmt(targetThk, 2)} mm`
          : "target thk from SO / 90%",
      in: { thk: curThk, width: curWidth || null, weight: curWeight },
      out: {
        thk: crmOut?.thickness ?? targetThk,
        width: curWidth || null,
        weight: crmOut?.weight ?? curWeight,
        qty: partCount,
      },
      waste: { weightT: 0, label: "≈0 (weight held)" },
    });

    if (crmOut) {
      curThk = crmOut.thickness;
      curWtmm = crmOut.wt_per_m;
      curWeight = crmOut.weight;
    }
  }

  journey.push({
    id: "anneal",
    name: "Annealing",
    status: "skipped",
    note: "Optional · not in draft",
    in: { thk: curThk, width: curWidth || null, weight: curWeight },
    out: { thk: curThk, width: curWidth || null, weight: curWeight },
    waste: { weightT: 0, label: "0 (skipped)" },
  });

  journey.push({
    id: "skin",
    name: "Skin pass",
    status: "skipped",
    note: "Optional · not in draft",
    in: { thk: curThk, width: curWidth || null, weight: curWeight },
    out: { thk: curThk, width: curWidth || null, weight: curWeight },
    waste: { weightT: 0, label: "0 (skipped)" },
  });

  const slit = machineFor(processTypes, machines, "CR_SLITTING");
  if (slit.processTypeId) {
    const shape = shapes.find((s) => s.id === line?.shape_size_id);
    const od = shape?.od ?? null;
    const tubeThk = shape?.thickness ?? line?.thickness ?? curThk;
    let nestNote = "Refine nest in stage assist";
    let stripW: number | null = null;
    let leftover = 0;
    let scrapPct = 0;
    let slitWeight = curWeight * 0.95;
    let stripCount = 1;

    if (od != null && od > 0 && tubeThk != null && tubeThk > 0 && curWidth > 0) {
      stripW = Math.round(stripWidthFromOd(od, tubeThk) * 10) / 10;
      const patterns = nestStrips({
        coilWidth: curWidth,
        demands: [{ stripWidth: stripW, count: 12, label: shape?.size_label }],
        constants: DEFAULT_MILL_CONSTANTS,
      });
      const best = patterns[0];
      if (best) {
        leftover = best.leftover;
        scrapPct = best.scrapPct;
        stripCount = best.orderedStripCount || 1;
        const used = best.usedWidth;
        const usable = curWidth - 2 * DEFAULT_MILL_CONSTANTS.edge_trim_mm;
        slitWeight = usable > 0 ? curWeight * (used / usable) : curWeight;
        const edgeTrimWt =
          curWidth > 0
            ? (2 * DEFAULT_MILL_CONSTANTS.edge_trim_mm * curWeight) / curWidth
            : 0;
        const leftoverWt = curWidth > 0 ? (leftover * curWeight) / curWidth : 0;
        totalWasteT += edgeTrimWt + leftoverWt;
        nestNote = `${stripCount}× ${fmt(stripW, 1)} mm` + (shape?.size_label ? ` · ${shape.size_label}` : "");
      }
    }

    const slitWasteW =
      leftover + (curWidth > 0 ? 2 * DEFAULT_MILL_CONSTANTS.edge_trim_mm : 0);
    const slitWasteT = Math.max(0, curWeight - slitWeight);

    stages.push({
      ...emptyPlanStage,
      process_type_id: slit.processTypeId,
      machine_id: slit.machineId,
      input_source: "previous_stage",
      inputs: [],
      quality_check_required: true,
      outputs: [
        {
          ...emptyPlanOutput,
          material_id: matId,
          shape_size_id: line?.shape_size_id ?? "",
          planned_qty: stripCount,
          planned_weight: slitWeight,
          thickness_mm: curThk ?? undefined,
          width_mm: stripW ?? undefined,
          notes: nestNote,
        },
      ],
    });

    journey.push({
      id: "slit",
      name: "CR Slitting",
      status: "in_draft",
      note: nestNote,
      in: { thk: curThk, width: curWidth || null, weight: curWeight },
      out: {
        thk: curThk,
        width: stripW,
        weight: slitWeight,
        qty: stripCount,
        label: stripW != null ? `${stripCount} strips` : "strips",
      },
      waste: {
        widthMm: slitWasteW || undefined,
        weightT: slitWasteT,
        pct: scrapPct || undefined,
        label:
          stripW == null
            ? "edge + nest — set OD / assist"
            : undefined,
      },
      detailLines:
        stripW != null
          ? [
              `Strip ${fmt(stripW, 1)} mm × ${stripCount}`,
              `Leftover ${fmt(leftover, 1)} mm · edge trim ${DEFAULT_MILL_CONSTANTS.edge_trim_mm} mm/side`,
            ]
          : ["No tube OD on SO line — open nest assist after accept"],
    });

    curWeight = slitWeight;
    if (stripW != null) curWidth = stripW;
  }

  const tube = machineFor(processTypes, machines, "TUBE");
  if (tube.processTypeId && line) {
    stages.push({
      ...emptyPlanStage,
      process_type_id: tube.processTypeId,
      machine_id: tube.machineId,
      input_source: "previous_stage",
      inputs: [],
      quality_check_required: true,
      outputs: [
        {
          ...emptyPlanOutput,
          material_id: matId,
          shape_size_id: line.shape_size_id ?? "",
          planned_qty: line.qty || 1,
          planned_weight: line.weight || 0,
          thickness_mm: line.thickness ?? undefined,
          length_mm: line.length ?? undefined,
          notes: "Suggested tube from SO line",
        },
      ],
    });

    journey.push({
      id: "tube",
      name: "Tube",
      status: "fg",
      note: "From SO line",
      in: { thk: curThk, width: curWidth || null, weight: curWeight },
      out: {
        thk: line.thickness ?? null,
        weight: line.weight || 0,
        qty: line.qty || 1,
        label: "FG",
      },
      waste: { label: "see CTL if cut" },
    });

    if (machineFor(processTypes, machines, "CTL").processTypeId) {
      journey.push({
        id: "ctl",
        name: "CTL",
        status: "skipped",
        note: "Optional · add if needed",
        waste: { label: "blade + yield margin" },
      });
    }
  } else if (stages.length) {
    journey.push({
      id: "end",
      name: "Stock / next",
      status: "fg",
      note: "No SO line — edit freely",
      out: { weight: curWeight, width: curWidth || null, thk: curThk },
    });
  }

  if (!stages.length) return { error: "Process types / machines missing for mill chain" };

  return {
    stages,
    mother: {
      batchNo: coil.batch_no,
      weight: coil.weight,
      thk: coilThk,
      width: coilWidth || null,
      wtPerMm: wtmm,
      alreadyPickled,
    },
    salesOrderId: salesOrder?.id,
    salesOrderLineId: line?.id,
    soNumber: salesOrder?.so_number,
    journey,
    draftStageCount: stages.length,
    totalWasteT,
  };
}

function statusBadge(status: JourneyStep["status"]) {
  switch (status) {
    case "stock":
      return { text: "STOCK", className: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    case "in_draft":
      return { text: "DRAFT", className: "bg-sky-50 text-sky-800 border-sky-200" };
    case "fg":
      return { text: "FG", className: "bg-indigo-50 text-indigo-800 border-indigo-200" };
    case "skipped":
      return { text: "SKIP", className: "bg-muted text-muted-foreground border-border" };
  }
}

/**
 * Opens overlay with mother-coil → FG journey (numbers + wastage). Accept prefills plan stages.
 */
export function SuggestPlanButton({
  form,
  processTypes,
  machines,
  materials,
  stockBatches,
  salesOrder,
  shapes = [],
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  processTypes: ProcessType[];
  machines: Machine[];
  materials: Material[];
  stockBatches: StockBatch[];
  salesOrder?: SalesOrder;
  shapes?: ShapeSize[];
}) {
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);

  function openOverlay() {
    const result = buildSuggestion({
      processTypes,
      machines,
      materials,
      stockBatches,
      salesOrder,
      shapes,
    });
    if ("error" in result) {
      toast.error(result.error);
      return;
    }
    setSuggestion(result);
    setOpen(true);
  }

  function accept() {
    if (!suggestion) return;
    form.setValue("stages", suggestion.stages);
    if (suggestion.salesOrderId) form.setValue("sales_order_id", suggestion.salesOrderId);
    if (suggestion.salesOrderLineId) {
      form.setValue("sales_order_line_id", suggestion.salesOrderLineId);
    }
    setOpen(false);
    toast.success(
      `Draft ${suggestion.draftStageCount} stages loaded — edit freely before save`,
    );
  }

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={openOverlay}>
        <Sparkles className="size-3.5" />
        Suggest plan
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl gap-0 overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 pr-10">
            <DialogHeader>
              <DialogTitle>Suggested coil journey</DialogTitle>
              <DialogDescription className="text-2xs">
                Mother → each stage with numbers and wastage · Accept fills form (editable)
              </DialogDescription>
            </DialogHeader>
          </div>

          {suggestion ? (
            <div className="overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 border-b border-border bg-muted/30 px-4 py-2 text-2xs sm:grid-cols-4">
                <div>
                  <span className="text-muted-foreground">Mother </span>
                  <span className="font-medium tabular-nums">{suggestion.mother.batchNo}</span>
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">Thk </span>
                  {fmt(suggestion.mother.thk, 2)} mm
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">Width </span>
                  {fmt(suggestion.mother.width, 1)} mm
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">Wt </span>
                  {fmt(suggestion.mother.weight, 3)} T
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">wt/mm </span>
                  {fmt(suggestion.mother.wtPerMm, 3)}
                </div>
                <div>
                  <span className="text-muted-foreground">SO </span>
                  {suggestion.soNumber ? (
                    <span className="font-medium">{suggestion.soNumber}</span>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">Draft stages </span>
                  {suggestion.draftStageCount}
                </div>
                <div className="tabular-nums">
                  <span className="text-muted-foreground">Σ waste ≈ </span>
                  <span className="font-medium">{fmt(suggestion.totalWasteT, 3)} T</span>
                </div>
              </div>

              <table className="w-full border-collapse text-2xs">
                <thead>
                  <tr className="border-b border-border bg-muted/20 text-left text-muted-foreground">
                    <th className="w-8 px-2 py-1.5 font-medium">#</th>
                    <th className="px-2 py-1.5 font-medium">Stage</th>
                    <th className="w-14 px-2 py-1.5 font-medium">Status</th>
                    <th className="px-2 py-1.5 font-medium">In</th>
                    <th className="px-2 py-1.5 font-medium">Out</th>
                    <th className="min-w-[7rem] px-2 py-1.5 font-medium">Waste</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestion.journey.map((step, i) => {
                    const badge = statusBadge(step.status);
                    return (
                      <tr
                        key={step.id}
                        className={cn(
                          "border-b border-border align-top",
                          step.status === "skipped" && "opacity-60",
                          step.status === "stock" && "bg-emerald-50/40",
                          step.status === "fg" && "bg-indigo-50/30",
                        )}
                      >
                        <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-foreground">{step.name}</div>
                          {step.note ? (
                            <div className="text-muted-foreground">{step.note}</div>
                          ) : null}
                          {step.detailLines?.length ? (
                            <ul className="mt-0.5 max-h-16 overflow-y-auto tabular-nums text-muted-foreground">
                              {step.detailLines.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5">
                          <span
                            className={cn(
                              "inline-block rounded border px-1 py-px text-[10px] font-semibold tracking-wide",
                              badge.className,
                            )}
                          >
                            {badge.text}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 tabular-nums text-foreground/90">
                          {dimsLine(step.in)}
                        </td>
                        <td className="px-2 py-1.5 tabular-nums text-foreground/90">
                          {dimsLine(step.out)}
                        </td>
                        <td
                          className={cn(
                            "px-2 py-1.5 tabular-nums",
                            step.waste?.weightT && step.waste.weightT > 0
                              ? "font-medium text-amber-800"
                              : "text-muted-foreground",
                          )}
                        >
                          {wasteLine(step.waste)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          <DialogFooter className="border-t border-border px-4 py-2.5">
            <Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={accept} disabled={!suggestion}>
              <Check className="size-3.5" />
              Accept & fill form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
